import { clamp, motion } from 'framer-motion'
import { useIntersectionObserver } from 'usehooks-ts'

import Counter from '@/components/counter'
import RichText from '@/components/ui/rich-text'
import ImageContentful from './ui/image-contentful'
import { cx } from '@/utils/strings'
import { TypeComponentCta, TypeSectionBarGraph } from '@/types/ctf'

export type AverageMarkerProps = {
  averageValue: number
  index: number
  isCompact?: boolean
}

export function AverageMarker({ averageValue, index, isCompact }: AverageMarkerProps) {
  return (
    <motion.div
      className={cx('w-[2px] flex flex-col items-center absolute z-10 top-[calc(-50%-4px)]')}
      style={{ left: `${averageValue}%` }}
      variants={{
        0: { opacity: 0, translateY: -30, translateX: '-50%' },
        100: { opacity: 1, translateY: 0, translateX: '-50%' },
      }}
      transition={{ duration: 0.4, ease: 'easeOut', delay: 1.5 + (index + 1) * 0.1 }}
    >
      <span className={cx('text-eyebrow uppercase whitespace-nowrap', !isCompact && '-translate-y-2')}>
        AVG: {averageValue}%
      </span>
      <div className={cx('w-[3px] rounded-full bg-accent-teal h-[56px]')} />
    </motion.div>
  )
}

export type BarGraphItemProps = {
  id?: string
  value: number
  averageValue?: number
  logoIcon?: any
  index: number
  isActive?: boolean
  isCompact?: boolean
}

export function BarGraphItem({ value, averageValue, index, logoIcon, isActive, isCompact }: BarGraphItemProps) {
  return (
    <div className="flex items-center">
      {logoIcon && (
        <div className="flex justify-center items-center w-8 h-8 md:w-12 md:h-12 mr-4 md:mr-6">
          <ImageContentful {...logoIcon.fields} />
        </div>
      )}
      <div className="relative flex items-center flex-1">
        {averageValue && <AverageMarker averageValue={averageValue} index={index} isCompact={isCompact} />}
        <div className={cx('relative w-full rounded-lg overflow-hidden bg-white dark:bg-secondary-800 h-[48px]')}>
          <motion.div
            style={{ width: `${value}%` }}
            className={cx('absolute top-0 left-0 h-full bg-space-gradient origin-left')}
            variants={{
              0: { translateX: '-100%' },
              100: { translateX: 0 },
            }}
            transition={{ duration: 1.5, ease: 'anticipate' }}
          />
        </div>
      </div>
      <div className="relative ml-4">
        <Counter
          className="font-heading absolute top-0 bottom-0 left-0 right-0 m-auto w-full h-[40px] text-2xl"
          from={0}
          to={value}
          duration={1.5}
          postfix="%"
          isActive={isActive}
        />
      </div>
    </div>
  )
}

export type BarGraphProps = TypeSectionBarGraph<'WITHOUT_UNRESOLVABLE_LINKS'>['fields'] & {
  eyebrowText?: string
  subtext?: string
  ctas?: TypeComponentCta<'WITHOUT_UNRESOLVABLE_LINKS'>[]
}

export default function BarGraph({ eyebrowText, subtext, headline, stats: items, bottomCaption }: BarGraphProps) {
  // scale the intersection threshold based on the number of items
  const threshold = clamp(0.01, 0.5, 0.5 - ((items?.length ?? 0) - 3) * 0.04)
  const { entry, ref } = useIntersectionObserver({
    threshold,
    freezeOnceVisible: true,
  })
  const isVisible = !!entry?.isIntersecting

  return (
    <div ref={ref} className={cx('bg-secondary-50 dark:bg-secondary-900', !headline && 'pt-8 lg:pt-16')}>
      {(eyebrowText || subtext || headline) && (
        <div className="flex flex-col gap-3 text-center px-8 md:mb-16">
          {eyebrowText && <p className="text-eyebrow uppercase mb-0 text-sm">{eyebrowText}</p>}
          {headline && <h2 className="mb-0 text-3xl md:text-[2.5rem]">{headline}</h2>}
          {subtext && <div className="mb-0 text-lg">{subtext}</div>}
        </div>
      )}
      <motion.div
        className="flex flex-col gap-8 pt-0 pr-[90px] pb-[77px] pl-[40px] lg:gap-12 lg:pl-[120px] lg:pr-[160px]"
        transition={{ staggerChildren: 0.1 }}
        initial="0"
        animate={isVisible ? '100' : '0'}
      >
        {items?.map((item, index) => {
          if (!item) return null
          const {
            fields: {
              label,
              value,
              averageValue,
              averageValue2,
              averageValue3,
              logoIcon,
              logoIcon2,
              logoIcon3,
              value2,
              value3,
            },
            sys: { id },
          } = item
          return (
            <div key={id || label}>
              <h4 className={cx('mb-9 md:mb-4', logoIcon && 'ml-8 md:ml-[72px]')}>{label}</h4>
              <div className={cx('flex flex-col', averageValue2 || averageValue3 ? 'gap-y-8' : 'gap-y-6')}>
                <BarGraphItem
                  index={index}
                  value={value}
                  averageValue={averageValue}
                  logoIcon={logoIcon}
                  isActive={isVisible}
                  isCompact={!!(value2 || value3)}
                />
                {value2 && (
                  <BarGraphItem
                    index={index}
                    value={value2}
                    averageValue={averageValue2}
                    logoIcon={logoIcon2}
                    isActive={isVisible}
                    isCompact
                  />
                )}
                {value3 && (
                  <BarGraphItem
                    index={index}
                    value={value3}
                    averageValue={averageValue3}
                    logoIcon={logoIcon3}
                    isActive={isVisible}
                    isCompact
                  />
                )}
              </div>
            </div>
          )
        })}
      </motion.div>

      {bottomCaption?.content && (
        <div className="text-center pb-4 [a]:text-tealBlue">
          <RichText body={bottomCaption} />
        </div>
      )}
    </div>
  )
}
